/**
 * Real-user monitoring (QM-NX-003). Sends LCP / INP / CLS / FCP / TTFB for the current ROUTE PATTERN only:
 * ids and anything that is not a known static segment are replaced by ":id"; no query strings, names,
 * emails or tenant identifiers ever leave the device (security scenario "RUM privacy").
 */
import type { RumBeacon } from '@qm/contracts/shell';

const STATIC_SEGMENTS = new Set(['audit', 'leads', 'inbox', 'calendar', 'approvals', 'ads', 'content', 'branding', 'team', 'billing', 'notifications', 'settings', 'reports', 'signin', 'signup', 'sign-in', 'link', 'purchase', 'status', 'invoices']);

export function routePattern(pathname: string): string {
  const clean = pathname.replace(/[?#].*$/, '');
  const segs = clean.split('/').filter(Boolean).slice(0, 6);
  return '/' + segs.map((s) => (STATIC_SEGMENTS.has(s.toLowerCase()) ? s.toLowerCase() : ':id')).join('/');
}

export function deviceClass(width: number): RumBeacon['device'] {
  return width < 768 ? 'mobile' : width < 1280 ? 'tablet' : 'desktop';
}

export function beacon(metric: RumBeacon['metric'], value: number, pathname: string, width: number, connection?: string): RumBeacon {
  const conn = (['4g', '3g', 'slow-2g', '2g', 'wifi'] as const).find((c) => c === connection) ?? 'unknown';
  const v = metric === 'CLS' ? Math.round(value * 1000) / 1000 : Math.round(value);
  return { metric, value: Math.max(0, v), route: routePattern(pathname), device: deviceClass(width), connection: conn };
}

/** Browser wiring: PerformanceObserver -> one batched sendBeacon to the BFF on pagehide. */
export function startRum(send: (body: string) => void = (b) => { navigator.sendBeacon?.('/bff/v1/shell/rum', new Blob([b], { type: 'application/json' })); }): void {
  if (typeof PerformanceObserver === 'undefined') return;
  const queue: RumBeacon[] = [];
  const conn = () => (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType;
  const push = (m: RumBeacon['metric'], v: number) => { queue.push(beacon(m, v, location.pathname, window.innerWidth, conn())); };
  let cls = 0;
  let inp = 0;
  let lcp = 0;
  const observe = (type: string, cb: (e: PerformanceEntry) => void) => {
    try { new PerformanceObserver((l) => l.getEntries().forEach(cb)).observe({ type, buffered: true } as PerformanceObserverInit); } catch { /* unsupported */ }
  };
  observe('largest-contentful-paint', (e) => { lcp = e.startTime; });
  observe('layout-shift', (e) => { const x = e as PerformanceEntry & { value: number; hadRecentInput: boolean }; if (!x.hadRecentInput) cls += x.value; });
  observe('event', (e) => { inp = Math.max(inp, e.duration); });
  observe('paint', (e) => { if (e.name === 'first-contentful-paint') push('FCP', e.startTime); });
  addEventListener('pagehide', () => {
    if (lcp) push('LCP', lcp);
    push('CLS', cls);
    if (inp) push('INP', inp);
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav) push('TTFB', nav.responseStart);
    if (queue.length) send(JSON.stringify({ beacons: queue.splice(0, 20) }));
  }, { once: true });
}
